import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LoadingBuffer from "../components/LoadingBuffer";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.vanyacollectionsrajahmundry.com";

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    fullName: "",
    phone: "",
    address: "",
  });
  const [saveSuccess, setSaveSuccess] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      setError("User not logged in");
      setLoading(false);
      return;
    }

    let parsedUser;
    try {
      parsedUser = JSON.parse(storedUser);
    } catch (e) {
      setError("Session expired. Please log in again.");
      setLoading(false);
      return;
    }

    const userId = parsedUser?.id || parsedUser?.userId;

    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/users/${userId}`);
        if (!res.ok) throw new Error("Failed to fetch user data");
        const data = await res.json();

        const userObj = {
          id: data.id,
          name: data.full_name || parsedUser.fullName || parsedUser.name || "Royal Customer",
          email: data.email || parsedUser.email || "",
          memberSince: new Date(data.created_at || Date.now()).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          }),
          phone: data.phone || parsedUser.phone || "",
          address: data.address || parsedUser.address || "",
        };

        setUser(userObj);
        setEditFormData({
          fullName: userObj.name,
          phone: userObj.phone,
          address: userObj.address,
        });

        // Update localStorage user with latest phone & name
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...parsedUser,
            fullName: userObj.name,
            name: userObj.name,
            phone: userObj.phone,
            address: userObj.address,
          })
        );
      } catch (err) {
        console.error(err);
        // Fallback to localStorage data
        if (parsedUser) {
          const userObj = {
            id: parsedUser.id,
            name: parsedUser.fullName || parsedUser.name || "Royal Customer",
            email: parsedUser.email || "",
            memberSince: "Valued Member",
            phone: parsedUser.phone || "",
            address: parsedUser.address || "",
          };
          setUser(userObj);
          setEditFormData({
            fullName: userObj.name,
            phone: userObj.phone,
            address: userObj.address,
          });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event("vanya_auth_change"));
    navigate("/login");
  };

  const goToOrders = () => {
    navigate("/orders");
  };

  const goToTracking = () => {
    navigate("/track-order");
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    setSaveSuccess("");

    try {
      const hasExistingPhone = Boolean(
        user.phone && String(user.phone).replace(/\D/g, "").slice(-10).length === 10
      );

      let phoneToSend = undefined;
      let cleanNewPhone = "";

      if (!hasExistingPhone) {
        // First-time mobile number setup for older account
        const rawInput = (editFormData.phone || "").trim();
        cleanNewPhone = rawInput.replace(/\D/g, "").slice(-10);

        if (!cleanNewPhone || cleanNewPhone.length !== 10) {
          throw new Error("Please enter a valid 10-digit Indian mobile number");
        }
        phoneToSend = cleanNewPhone;
      }

      const res = await fetch(`${API_BASE}/auth/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: editFormData.fullName.trim(),
          phone: phoneToSend,
          address: editFormData.address.trim(),
        }),
      });

      const data = await res.json();
      setSaveLoading(false);

      if (!res.ok) {
        throw new Error(data.message || "Failed to update profile");
      }

      const finalPhone = hasExistingPhone ? user.phone : cleanNewPhone;

      const updatedUser = {
        ...user,
        name: editFormData.fullName.trim(),
        phone: finalPhone,
        address: editFormData.address.trim(),
      };

      setUser(updatedUser);
      setIsEditing(false);
      setSaveSuccess(
        !hasExistingPhone && cleanNewPhone
          ? "Mobile number linked permanently & profile updated successfully! ✨"
          : "Profile details updated successfully! ✨"
      );

      // Update localStorage
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...parsed,
            fullName: updatedUser.name,
            name: updatedUser.name,
            phone: updatedUser.phone,
            address: updatedUser.address,
          })
        );
        window.dispatchEvent(new Event("storage"));
        window.dispatchEvent(new Event("vanya_auth_change"));
      }

      setTimeout(() => setSaveSuccess(""), 5000);
    } catch (err) {
      setSaveLoading(false);
      alert(err.message || "Error updating profile");
    }
  };

  if (loading)
    return (
      <LoadingBuffer 
        message="Loading Your Royal Profile..." 
        fullPage={true} 
      />
    );
  if (error)
    return <div className="profile-loading" style={{ color: "#c62828" }}>{error}</div>;
  if (!user) return null;

  const hasExistingPhone = Boolean(
    user.phone && String(user.phone).replace(/\D/g, "").slice(-10).length === 10
  );

  return (
    <div style={styles.pageContainer}>
      <style>{hoverEffects}</style>

      <div style={styles.contentWrapper}>
        {/* Avatar Section */}
        <div style={styles.avatarCircle}>{user.name ? user.name.charAt(0).toUpperCase() : "V"}</div>

        <h1 style={styles.userName}>{user.name}</h1>
        <p style={styles.membershipText}>
          ✨ Gold Member since {user.memberSince} ✨
        </p>

        {saveSuccess && (
          <div
            style={{
              background: "#e8f5e9",
              color: "#1b5e20",
              border: "1px solid #c8e6c9",
              borderRadius: "12px",
              padding: "10px 16px",
              marginBottom: "20px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            {saveSuccess}
          </div>
        )}

        {/* Profile Details or Edit Form */}
        {!isEditing ? (
          <>
            {/* Info Grid */}
            <div style={styles.infoGrid}>
              <div style={styles.infoCard}>
                <span style={styles.label}>EMAIL ADDRESS</span>
                <p style={styles.value}>{user.email || "Not provided"}</p>
              </div>

              <div style={styles.infoCard}>
                <span style={styles.label}>
                  MOBILE NUMBER {hasExistingPhone && <span style={{ color: "#d4af37" }}>🔒</span>}
                </span>
                <div style={{ marginTop: "4px" }}>
                  {hasExistingPhone ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "6px" }}>
                      <span style={{ fontSize: "15px", fontWeight: "700", color: "#3b1443" }}>
                        📱 +91 {user.phone}
                      </span>
                      <span style={styles.lockedBadge}>
                        Permanent
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                      <span style={{ fontSize: "13.5px", color: "#b91c1c", fontWeight: "600" }}>
                        ⚠️ Not provided
                      </span>
                      <button
                        onClick={() => setIsEditing(true)}
                        style={styles.addPhoneMiniBtn}
                      >
                        + Add Mobile Number
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ ...styles.infoCard, gridColumn: "1 / -1" }}>
                <span style={styles.label}>SHIPPING ADDRESS</span>
                <p style={{ ...styles.value, color: user.address ? "#3b1443" : "#888" }}>
                  {user.address || "Not provided (will be saved when placing an order)"}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={styles.buttonGroup}>
              <button
                style={styles.primaryBtn}
                className="btn-p"
                onClick={() => setIsEditing(true)}
              >
                ✏️ Edit Profile
              </button>
              <button
                style={styles.secondaryBtn}
                className="btn-s"
                onClick={goToTracking}
              >
                🚚 Track My Orders
              </button>
            </div>
          </>
        ) : (
          /* EDIT PROFILE FORM */
          <form
            onSubmit={handleSaveProfile}
            style={{
              textAlign: "left",
              background: "#faf6fd",
              border: "1.5px solid #e5d4ec",
              borderRadius: "16px",
              padding: "24px",
              marginBottom: "25px",
            }}
          >
            <h3 style={{ margin: "0 0 16px", color: "#513262", fontSize: "1.2rem" }}>
              Edit Royal Profile
            </h3>

            <div style={{ marginBottom: "14px" }}>
              <label style={styles.editLabel}>Full Name</label>
              <input
                type="text"
                value={editFormData.fullName}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, fullName: e.target.value })
                }
                style={styles.editInput}
                required
              />
            </div>

            {hasExistingPhone ? (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <label style={styles.editLabel}>Registered Mobile Number</label>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "#6a2e7c", background: "#f3eaf7", padding: "2px 8px", borderRadius: "10px" }}>
                    🔒 Locked • Cannot be changed
                  </span>
                </div>
                <input
                  type="text"
                  value={`+91 ${user.phone}`}
                  disabled
                  readOnly
                  style={{
                    ...styles.editInput,
                    backgroundColor: "#f5eef8",
                    color: "#555",
                    cursor: "not-allowed",
                    border: "1.5px dashed #d1b8dc",
                    fontWeight: "700",
                  }}
                />
                <span style={{ fontSize: "11.5px", color: "#777", display: "block", marginTop: "4px" }}>
                  Your registered mobile number is permanently tied to your account and cannot be modified.
                </span>
              </div>
            ) : (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <label style={styles.editLabel}>10-Digit Mobile Number (One-Time Setup)</label>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "#c53030", background: "#fde8e8", padding: "2px 8px", borderRadius: "10px" }}>
                    ⚠️ Cannot be changed once updated
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={styles.phonePrefix}>+91</span>
                  <input
                    type="tel"
                    maxLength={10}
                    value={editFormData.phone}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, phone: e.target.value.replace(/\D/g, "") })
                    }
                    style={{ ...styles.editInput, flex: 1 }}
                    placeholder="Enter 10-digit mobile number"
                    required
                  />
                </div>
                <span style={{ fontSize: "11.5px", color: "#856404", display: "block", marginTop: "4px" }}>
                  * You can set your mobile number once for this account. Once saved, it will be locked permanently and cannot be changed.
                </span>
              </div>
            )}

            <div style={{ marginBottom: "18px" }}>
              <label style={styles.editLabel}>Delivery Address</label>
              <textarea
                rows="3"
                value={editFormData.address}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, address: e.target.value })
                }
                style={{ ...styles.editInput, resize: "vertical" }}
                placeholder="Door no., street, area, city, pincode"
              />
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#eee",
                  border: "none",
                  borderRadius: "25px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saveLoading}
                style={{
                  flex: 2,
                  padding: "12px",
                  background: "#5c2069",
                  color: "#fff",
                  border: "none",
                  borderRadius: "25px",
                  fontWeight: 700,
                  cursor: saveLoading ? "not-allowed" : "pointer",
                }}
              >
                {saveLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "15px" }}>
          <button
            onClick={goToOrders}
            style={{
              background: "none",
              border: "none",
              color: "#513262",
              fontSize: "0.95rem",
              fontWeight: "600",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            📦 View Order History
          </button>

          <button
            style={styles.logoutBtn}
            className="btn-logout"
            onClick={handleLogout}
          >
            Sign Out of Account
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= CSS-in-JS =================
const styles = {
  pageContainer: {
    backgroundColor: "#f7f2f9",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "60px 20px",
    fontFamily: '"Playfair Display", serif, sans-serif',
    color: "#3b1443",
  },
  contentWrapper: {
    maxWidth: "650px",
    width: "100%",
    textAlign: "center",
    backgroundColor: "#ffffff",
    padding: "45px 35px",
    borderRadius: "24px",
    boxShadow: "0 10px 35px rgba(74, 25, 89, 0.1)",
    border: "1px solid rgba(106, 46, 124, 0.18)",
  },
  avatarCircle: {
    width: "110px",
    height: "110px",
    background: "linear-gradient(135deg, #5c2069, #45154f)",
    color: "#d4af37",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "2.8rem",
    fontWeight: "bold",
    margin: "0 auto 20px",
    boxShadow: "0 8px 25px rgba(92, 32, 105, 0.25)",
    border: "2px solid #d4af37",
  },
  userName: { 
    fontSize: "2.5rem", 
    color: "#3b1443", 
    marginBottom: "8px",
    fontWeight: "700" 
  },
  membershipText: {
    color: "#6a2e7c",
    fontSize: "0.95rem",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    marginBottom: "35px",
    fontWeight: "600",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
    textAlign: "left",
    marginBottom: "35px",
  },
  infoCard: {
    padding: "18px 20px",
    background: "linear-gradient(135deg, #ffffff 0%, #f9f5fb 100%)",
    border: "1px solid rgba(106, 46, 124, 0.18)",
    borderRadius: "14px",
    boxShadow: "0 4px 15px rgba(74, 25, 89, 0.04)",
  },
  label: {
    display: "block",
    fontSize: "11px",
    color: "#6a2e7c",
    letterSpacing: "1.5px",
    fontWeight: "700",
    marginBottom: "6px",
  },
  value: {
    margin: 0,
    fontSize: "15px",
    color: "#3b1443",
    fontWeight: "600",
    wordBreak: "break-word",
  },
  buttonGroup: { 
    display: "flex", 
    gap: "15px", 
    marginBottom: "25px" 
  },
  primaryBtn: {
    flex: 1,
    padding: "16px",
    background: "linear-gradient(135deg, #5c2069, #45154f)",
    color: "#ffffff",
    border: "none",
    borderRadius: "40px",
    fontSize: "1rem",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 6px 20px rgba(92, 32, 105, 0.3)",
    transition: "all 0.3s ease",
  },
  secondaryBtn: {
    flex: 1,
    padding: "16px",
    background: "linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%)",
    color: "#3b1443",
    border: "1px solid #f3e5ab",
    borderRadius: "40px",
    fontSize: "1rem",
    fontWeight: "800",
    cursor: "pointer",
    boxShadow: "0 6px 20px rgba(212, 175, 55, 0.35)",
    transition: "all 0.3s ease",
  },
  logoutBtn: {
    backgroundColor: "transparent",
    border: "none",
    color: "#8b263e",
    fontSize: "0.95rem",
    fontWeight: "700",
    cursor: "pointer",
    textDecoration: "underline",
    letterSpacing: "0.5px",
  },
  editLabel: {
    display: "block",
    fontSize: "12px",
    fontWeight: "700",
    color: "#513262",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  editInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 14px",
    borderRadius: "10px",
    border: "1.5px solid #d8c3e0",
    fontSize: "14px",
    outline: "none",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  lockedBadge: {
    background: "#f3eaf7",
    color: "#513262",
    fontSize: "11px",
    fontWeight: "700",
    padding: "3px 8px",
    borderRadius: "12px",
    border: "1px solid #d8c3e0",
    letterSpacing: "0.5px",
  },
  addPhoneMiniBtn: {
    background: "linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%)",
    color: "#513262",
    border: "none",
    padding: "6px 14px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(212, 175, 55, 0.3)",
    whiteSpace: "nowrap",
  },
  phonePrefix: {
    background: "#f4ecf8",
    border: "1.5px solid #d8c3e0",
    borderRadius: "10px",
    padding: "11px 14px",
    fontWeight: "700",
    color: "#513262",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
  },
};

const hoverEffects = `
  .profile-loading {
    text-align: center;
    padding: 100px;
    font-size: 20px;
    color: #6a2e7c;
    background: #f7f2f9;
    min-height: 100vh;
    font-family: 'Playfair Display', serif;
  }
  .btn-p:hover { 
    background: linear-gradient(135deg, #6a2e7c, #5c2069); 
    transform: translateY(-2px); 
    box-shadow: 0 8px 25px rgba(92, 32, 105, 0.4);
  }
  .btn-s:hover { 
    background: linear-gradient(135deg, #e2be42, #b89832); 
    transform: translateY(-2px); 
    box-shadow: 0 8px 25px rgba(212, 175, 55, 0.5);
  }
  .btn-logout:hover {
    color: #6b1b2f;
  }
`;

export default ProfilePage;